import React, { useState, useEffect, useCallback } from "react";
import Modal from "../../Modal/Modal";
import {
  Calendar,
  Users,
  Gamepad2,
  MoreVertical,
  ListChecks,
  RefreshCw,
} from "lucide-react";
import ClanEventTeamsTab from "./ClanEventTeamsTab/ClanEventTeamsTab";
import ClanEventGameTab from "./ClanEventGameTab/ClanEventGameTab";
import ClanEventGameTasksTab from "./ClanEventGameTasksTab/ClanEventGameTasksTab";
import "./ClanEventsTab.css";

const DEFAULT_EVENT_TYPE = "Snakes and Ladders";
const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;
const FADE_OUT_DELAY_MS = 5000;
const FADE_OUT_DURATION_MS = 1000;

const formatDateTime = (dt_str) => {
  if (!dt_str) return "N/A";
  try {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    const dateToParse =
      dt_str.endsWith("Z") || dt_str.includes("+")
        ? dt_str
        : `${dt_str.replace(/\.\d+Z?$/, "")}Z`;
    return new Date(dateToParse).toLocaleString(undefined, options);
  } catch {
    return dt_str.replace("T", " ");
  }
};

const getStatus = (start_dt_str, end_dt_str) => {
  const ensureUtc = (dt_str) => {
    if (!dt_str) return null;
    const dateToParse =
      dt_str.endsWith("Z") || dt_str.includes("+")
        ? dt_str
        : `${dt_str.replace(/\.\d+Z?$/, "")}Z`;
    return new Date(dateToParse);
  };

  const start_dt = ensureUtc(start_dt_str);
  const end_dt = end_dt_str ? ensureUtc(end_dt_str) : null;
  const now = new Date();

  if (end_dt && end_dt < now) {
    return <span className="event-status status-past">Completed</span>;
  }
  if (start_dt <= now) {
    return (
      <span className="event-status status-live-ongoing">Live / Ongoing</span>
    );
  }
  return <span className="event-status status-upcoming">Upcoming</span>;
};

const convertUtcToCstDisplay = (dt_local_str) => {
  if (!dt_local_str) return null;
  try {
    const utc_iso_string = `${dt_local_str}:00Z`;
    const utc_date = new Date(utc_iso_string);
    const cst_options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Chicago",
      timeZoneName: "short",
    };
    return utc_date.toLocaleString("en-US", cst_options);
  } catch (e) {
    console.error("CST conversion failed:", e);
    return "Invalid Time";
  }
};

const toDateTimeLocal = (dt_str) => {
  if (!dt_str) return "";
  const dateToParse =
    dt_str.endsWith("Z") || dt_str.includes("+")
      ? dt_str
      : `${dt_str.replace(/\.\d+Z?$/, "")}Z`;
  const dt = new Date(dateToParse);
  const year = dt.getUTCFullYear();
  const month = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dt.getUTCDate()).padStart(2, "0");
  const hour = String(dt.getUTCHours()).padStart(2, "0");
  const minute = String(dt.getUTCMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

const isEndBeforeStart = (start_dt_str, end_dt_str) => {
  if (!start_dt_str || !end_dt_str || end_dt_str.trim() === "") {
    return false;
  }
  const start_dt = new Date(`${start_dt_str}:00Z`);
  const end_dt = new Date(`${end_dt_str}:00Z`);
  return end_dt <= start_dt;
};

const getConfirmClassName = (baseClass, isDisabled) => {
  return isDisabled ? `${baseClass} btn-disabled` : baseClass;
};

const EventFormContent = ({
  data,
  onChange,
  isEdit = false,
  loading,
  apiMessage,
}) => {
  const currentApiMessage =
    apiMessage.type === "error" ? apiMessage.text : null;

  const minCurrentDatetime = new Date().toISOString().slice(0, 16);
  const minEndDatetime = data.start_datetime || minCurrentDatetime;
  const isInvalidEndDatetime = isEndBeforeStart(
    data.start_datetime,
    data.end_datetime,
  );
  const startCSTDisplay = convertUtcToCstDisplay(data.start_datetime);
  const endCSTDisplay = convertUtcToCstDisplay(data.end_datetime);

  return (
    <div className="create-event-form-container">
      {loading && (
        <p className="loading-message">
          {isEdit ? "Updating event..." : "Scheduling event..."}
        </p>
      )}
      {currentApiMessage && (
        <p className="form-error-message">{currentApiMessage}</p>
      )}

      <p className="modal-form-intro">
        {isEdit
          ? `Editing Event ID: ${data.id}`
          : `Schedule a ${DEFAULT_EVENT_TYPE} for your clan.`}
      </p>

      <div className="modal-form-group">
        <label htmlFor="name" className="modal-form-label">
          Event Name:
        </label>
        <input
          id="name"
          name="name"
          type="text"
          className="modal-form-field"
          value={data.name}
          onChange={onChange}
          placeholder="e.g., Snakes and Ladders Showdown"
          maxLength={100}
          disabled={loading}
        />
      </div>

      <input
        type="hidden"
        name="type"
        value={data.type || DEFAULT_EVENT_TYPE}
      />

      <div className="modal-form-group">
        <label htmlFor="start_datetime" className="modal-form-label">
          Start Date & Time (UTC):
        </label>
        <input
          id="start_datetime"
          name="start_datetime"
          type="datetime-local"
          className="modal-form-field"
          value={data.start_datetime}
          onChange={onChange}
          min={minCurrentDatetime}
          disabled={loading}
        />
        {startCSTDisplay && (
          <p className="conversion-note">
            Central Time (CST/CDT): <strong>{startCSTDisplay}</strong>
          </p>
        )}
      </div>

      <div className="modal-form-group">
        <label htmlFor="end_datetime" className="modal-form-label">
          End Date & Time (UTC, Optional):
        </label>
        <input
          id="end_datetime"
          name="end_datetime"
          type="datetime-local"
          className="modal-form-field"
          value={data.end_datetime}
          onChange={onChange}
          min={minEndDatetime}
          disabled={loading}
        />
        {endCSTDisplay && (
          <p className="conversion-note">
            Central Time (CST/CDT): <strong>{endCSTDisplay}</strong>
          </p>
        )}
        {isInvalidEndDatetime && (
          <p className="form-error-message form-inline-error">
            End Date/Time cannot be before or the same as the Start Date/Time.
          </p>
        )}
      </div>
    </div>
  );
};

const EventActionDropdown = ({
  event,
  loading,
  handleEditClick,
  handleDeleteClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const isCompleted = getStatus(
    event.start_datetime,
    event.end_datetime,
  ).props.className.includes("status-past");
  const isEditable = !isCompleted;

  const handleAction = (actionFunc) => {
    setIsOpen(false);
    actionFunc(event);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest(".event-action-dropdown-container") === null) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="event-action-dropdown-container">
      <button
        className="btn-icon-small"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        disabled={loading}
        title="More Actions"
      >
        <MoreVertical size={16} />
      </button>
      {isOpen && (
        <div className="event-action-dropdown-menu">
          {isEditable && (
            <button
              className="dropdown-item-button"
              onClick={(e) => {
                e.stopPropagation();
                handleAction(handleEditClick);
              }}
              disabled={loading}
            >
              Edit Event Details
            </button>
          )}
          <button
            className="dropdown-item-button dropdown-item-danger"
            onClick={(e) => {
              e.stopPropagation();
              handleAction(handleDeleteClick);
            }}
            disabled={loading}
          >
            Delete Event
          </button>
        </div>
      )}
    </div>
  );
};

function ClanEventsTab({ clanId, clanName }) {
  const [activeSubView, setActiveSubView] = useState("list");
  const [selectedEventId, setSelectedEventId] = useState(() => {
    return localStorage.getItem(`clan_events_${clanId}_event_id`);
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [apiMessage, setApiMessage] = useState({ type: null, text: null });
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newEventData, setNewEventData] = useState({
    name: "",
    start_datetime: "",
    end_datetime: "",
    type: DEFAULT_EVENT_TYPE,
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [eventToDelete, setEventToDelete] = useState(null);

  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    // Only run this when the events list has been populated
    if (!eventsLoading && events.length > 0) {
      const savedEventId = localStorage.getItem(
        `clan_events_${clanId}_event_id`,
      );
      const savedView = localStorage.getItem(`clan_events_${clanId}_subview`);

      if (savedEventId) {
        const found = events.find(
          (e) => e.id.toString() === savedEventId.toString(),
        );
        if (found) {
          setSelectedEvent(found);
          // Only switch view if we found the event
          if (savedView) {
            setActiveSubView(savedView);
          }
        } else {
          // Event no longer exists (e.g., deleted by another admin)
          localStorage.removeItem(`clan_events_${clanId}_event_id`);
          setActiveSubView("list");
        }
      }
    }
  }, [events, eventsLoading, clanId]);

  const updateSelectedEventGameId = useCallback((gameId) => {
    setSelectedEvent((prevEvent) => {
      if (prevEvent && prevEvent.game_id !== gameId) {
        return { ...prevEvent, game_id: gameId };
      }
      return prevEvent;
    });
  }, []);

  useEffect(() => {
    let fadeOutTimer;
    let clearMessageTimer;

    if (
      apiMessage.text &&
      apiMessage.type === "success" &&
      !isCreateModalOpen &&
      !isEditModalOpen &&
      !eventToDelete
    ) {
      setIsFadingOut(false);

      fadeOutTimer = setTimeout(() => {
        setIsFadingOut(true);

        clearMessageTimer = setTimeout(() => {
          setApiMessage({ type: null, text: null });
          setIsFadingOut(false);
        }, FADE_OUT_DURATION_MS);
      }, FADE_OUT_DELAY_MS);
    } else if (apiMessage.type === "error") {
      setIsFadingOut(false);
    }

    return () => {
      clearTimeout(fadeOutTimer);
      clearTimeout(clearMessageTimer);
    };
  }, [
    apiMessage.text,
    apiMessage.type,
    isCreateModalOpen,
    isEditModalOpen,
    eventToDelete,
  ]);

  const fetchEvents = useCallback(
    async (past) => {
      setEventsLoading(true);
      const url = `${API_BASE_URL}/clan/${clanId}/events/?past=${
        past ? "true" : "false"
      }`;

      try {
        const response = await fetch(url, { credentials: "include" });
        const data = await response.json();
        if (!response.ok) {
          const errorText = data.error || "Failed to fetch events.";
          setApiMessage({ type: "error", text: errorText });
          setEvents([]);
        } else {
          setEvents(data.events || []);

          if (selectedEvent) {
            const updatedEvent = (data.events || []).find(
              (e) => e.id === selectedEvent.id,
            );
            if (updatedEvent) {
              setSelectedEvent((prev) => ({
                ...prev,
                game_id: updatedEvent.game_id,
              }));
            }
          }
        }
      } catch (error) {
        setApiMessage({
          type: "error",
          text: "Network error fetching events.",
        });
        console.error("Event fetch network error:", error);
      } finally {
        setEventsLoading(false);
      }
    },
    [clanId],
  );

  useEffect(() => {
    if (clanId) {
      fetchEvents(showPastEvents);
    }
  }, [clanId, showPastEvents, fetchEvents]);

  const handleManualRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchEvents();
    setTimeout(() => setIsRefreshing(false), 600);
  }, [fetchEvents]);

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEventToEdit(null);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setNewEventData({
      name: "",
      start_datetime: "",
      end_datetime: "",
      type: DEFAULT_EVENT_TYPE,
    });
  };

  const closeDeleteModal = () => {
    setEventToDelete(null);
  };

  const executeCreateEvent = async () => {
    const isDisabled =
      loading ||
      !newEventData.name.trim() ||
      !newEventData.start_datetime ||
      isEndBeforeStart(newEventData.start_datetime, newEventData.end_datetime);

    if (isDisabled) {
      if (
        isEndBeforeStart(newEventData.start_datetime, newEventData.end_datetime)
      ) {
        setApiMessage({
          type: "error",
          text: "End Date/Time cannot be before or the same as the Start Date/Time.",
        });
      } else {
        setApiMessage({
          type: "error",
          text: "Please fill out all required fields (Event Name and Start Date/Time).",
        });
      }
      return;
    }

    setLoading(true);
    setApiMessage({ type: null, text: null });

    const url = `${API_BASE_URL}/clan/${clanId}/events/`;
    const requestBody = {
      name: newEventData.name.trim(),
      type: newEventData.type || DEFAULT_EVENT_TYPE,
      start_datetime: newEventData.start_datetime,
      end_datetime: newEventData.end_datetime || null,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        const errorText =
          data.error || "Failed to schedule event due to server error.";
        setApiMessage({ type: "error", text: errorText });
      } else {
        closeCreateModal();
        setApiMessage({
          type: "success",
          text: data.message || `Event scheduled successfully!`,
        });
        fetchEvents(showPastEvents);
      }
    } catch (error) {
      setApiMessage({
        type: "error",
        text: "Network error. Failed to connect to the server.",
      });
      console.error("Event creation network error:", error);
    } finally {
      setLoading(false);
    }
  };

  const executeEditEvent = async () => {
    const isDisabled =
      loading ||
      !eventToEdit.name.trim() ||
      !eventToEdit.start_datetime ||
      isEndBeforeStart(eventToEdit.start_datetime, eventToEdit.end_datetime);

    if (isDisabled) {
      if (
        isEndBeforeStart(eventToEdit.start_datetime, eventToEdit.end_datetime)
      ) {
        setApiMessage({
          type: "error",
          text: "End Date/Time cannot be before or the same as the Start Date/Time.",
        });
      } else {
        setApiMessage({
          type: "error",
          text: "Please fill out all required fields (Event Name and Start Date/Time).",
        });
      }
      return;
    }

    setLoading(true);
    setApiMessage({ type: null, text: null });

    const event_id = eventToEdit.id;
    const url = `${API_BASE_URL}/clan/${clanId}/events/${event_id}`;

    const requestBody = {
      name: eventToEdit.name.trim(),
      type: eventToEdit.type || DEFAULT_EVENT_TYPE,
      start_datetime: eventToEdit.start_datetime,
      end_datetime: eventToEdit.end_datetime || null,
    };

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        const errorText =
          data.error || "Failed to update event due to server error.";
        setApiMessage({ type: "error", text: errorText });
      } else {
        closeEditModal();
        setApiMessage({
          type: "success",
          text:
            data.message || `Event '${data.event.name}' updated successfully.`,
        });
        fetchEvents(showPastEvents);
      }
    } catch (error) {
      setApiMessage({
        type: "error",
        text: "Network error. Failed to connect to the server.",
      });
      console.error("Event update network error:", error);
    } finally {
      setLoading(false);
    }
  };

  const executeDelete = async () => {
    if (!eventToDelete) return;
    const eventId = eventToDelete.id;
    const eventName = eventToDelete.name;

    closeDeleteModal();
    setApiMessage({ type: null, text: null });
    setEventsLoading(true);

    const url = `${API_BASE_URL}/clan/${clanId}/events/${eventId}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        const errorText =
          data.error || "Failed to delete event due to server error.";
        setApiMessage({ type: "error", text: errorText });
      } else {
        setApiMessage({
          type: "success",
          text: `Event '${eventName}' successfully deleted.`,
        });
        fetchEvents(showPastEvents);
      }
    } catch (error) {
      setApiMessage({
        type: "error",
        text: "Network error. Failed to connect to the server.",
      });
      console.error("Event deletion network error:", error);
    } finally {
      setEventsLoading(false);
    }
  };

  const handleToggleEventsView = () => {
    setShowPastEvents((prev) => !prev);
  };

  const handleCreateEventClick = () => {
    setApiMessage({ type: null, text: null });
    const nowUtcFormatted = toDateTimeLocal(new Date().toISOString());
    setNewEventData({
      name: "",
      start_datetime: nowUtcFormatted,
      end_datetime: "",
      type: DEFAULT_EVENT_TYPE,
    });
    setIsCreateModalOpen(true);
  };

  const handleEditClick = (event) => {
    setApiMessage({ type: null, text: null });
    setEventToEdit({
      ...event,
      start_datetime: toDateTimeLocal(event.start_datetime),
      end_datetime: toDateTimeLocal(event.end_datetime),
      type: event.type || DEFAULT_EVENT_TYPE,
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (event) => {
    setApiMessage({ type: null, text: null });
    setEventToDelete(event);
  };

  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;
    setNewEventData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEventToEdit((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const switchView = (viewName, event = null) => {
    setActiveSubView(viewName);

    localStorage.setItem(`clan_events_${clanId}_subview`, viewName);

    if (event) {
      setSelectedEvent(event);
      setSelectedEventId(event.id);
      localStorage.setItem(`clan_events_${clanId}_event_id`, event.id);
    } else if (viewName === "list") {
      setSelectedEvent(null);
      setSelectedEventId(null);
      localStorage.removeItem(`clan_events_${clanId}_event_id`);
    }
  };

  const handleManageTeams = (event) => switchView("manage_teams", event);

  const handleManageGame = (event) => switchView("manage_game", event);

  const handleManageGameTasks = (event) => {
    if (event && event.game_id) {
      switchView("manage_game_tasks", event);
    }
  };

  const goBackToList = () => switchView("list", null);

  const renderEventListContent = () => {
    if (eventsLoading) {
      return (
        <tr>
          <td className="table-placeholder-cell" colSpan="6">
            <small>
              Loading {showPastEvents ? "past" : "upcoming"} events...
            </small>
          </td>
        </tr>
      );
    }

    if (events.length === 0) {
      return (
        <tr>
          <td className="table-placeholder-cell" colSpan="6">
            <p className="no-events-message">
              {showPastEvents
                ? "No past events found for this clan."
                : 'No events currently scheduled. Click "Create New Event" to start!'}
            </p>
          </td>
        </tr>
      );
    }

    return (
      <>
        {events.map((event) => {
          return (
            <tr key={event.id}>
              <td data-label="Event Name" className="event-name-cell">
                {event.name}
              </td>
              <td data-label="Date & Time">
                {formatDateTime(event.start_datetime)}
                {event.end_datetime && (
                  <small className="event-end-time">
                    &nbsp;- {formatDateTime(event.end_datetime)}
                  </small>
                )}
              </td>
              <td data-label="Status">
                {getStatus(event.start_datetime, event.end_datetime)}
              </td>
              <td data-label="Teams" className="event-manage-teams-cell">
                <button
                  className="btn-small btn-secondary"
                  onClick={() => handleManageTeams(event)}
                  disabled={loading}
                >
                  <Users size={16} style={{ marginRight: "5px" }} /> Teams
                </button>
              </td>
              <td data-label="Game" className="event-manage-game-cell">
                <button
                  className="btn-small btn-secondary"
                  onClick={() => handleManageGame(event)}
                  disabled={loading}
                >
                  <Gamepad2 size={16} style={{ marginRight: "5px" }} /> Game
                </button>
              </td>
              <td data-label="Actions" className="event-actions-cell">
                <EventActionDropdown
                  event={event}
                  loading={loading}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                />
              </td>
            </tr>
          );
        })}
      </>
    );
  };

  const renderActiveSubView = () => {
    if (eventsLoading && activeSubView !== "list") {
      return <div className="loading-message">Loading event details...</div>;
    }

    const subViewHeader = selectedEvent && (
      <div className="active-event-header">
        Managing Event: <strong>{selectedEvent.name}</strong>
      </div>
    );

    switch (activeSubView) {
      case "manage_teams":
        if (!selectedEvent)
          return <p>Error: No event selected for team management.</p>;
        return (
          <div className="event-sub-view-container">
            {subViewHeader}
            <ClanEventTeamsTab
              clanId={clanId}
              event={selectedEvent}
              goBackToList={goBackToList}
            />
          </div>
        );
      case "manage_game":
        if (!selectedEvent)
          return <p>Error: No event selected for game management.</p>;
        return (
          <div className="event-sub-view-container">
            {subViewHeader}
            <ClanEventGameTab
              clanId={clanId}
              event={selectedEvent}
              goBackToList={goBackToList}
              onGameCreated={updateSelectedEventGameId}
            />
          </div>
        );
      case "manage_game_tasks":
        if (!selectedEvent || !selectedEvent.game_id)
          return (
            <p>
              Error: No game board found for this event. Please create one first
              via "Manage Game."
            </p>
          );
        return (
          <div className="event-sub-view-container">
            {subViewHeader}
            <ClanEventGameTasksTab
              clanId={clanId}
              event={selectedEvent}
              gameId={selectedEvent.game_id}
              goBackToList={goBackToList}
            />
          </div>
        );
      case "list":
      default:
        return (
          <>
            <div className="event-controls">
              <button
                className="btn-large btn-primary"
                disabled={loading}
                onClick={handleCreateEventClick}
              >
                Create New Event
              </button>
              <button
                className={`btn-large ${
                  showPastEvents ? "btn-success" : "btn-warning"
                }`}
                disabled={loading || eventsLoading}
                onClick={handleToggleEventsView}
              >
                {showPastEvents ? "View Upcoming Events" : "View Past Events"}
              </button>
            </div>
            <div className="event-list-placeholder">
              <h3 className="upcoming-events-title">
                {showPastEvents
                  ? "Past Events (Most Recent First)"
                  : "Upcoming Events (Soonest First)"}
              </h3>
              <table className="events-placeholder-table">
                <thead>
                  <tr className="table-header-row">
                    <th className="table-header">Event Name</th>
                    <th className="table-header">Date & Time</th>
                    <th className="table-header">Status</th>
                    <th className="table-header action-col">Teams</th>
                    <th className="table-header action-col">Game</th>
                    <th className="table-header action-col">Actions</th>
                  </tr>
                </thead>
                <tbody>{renderEventListContent()}</tbody>
              </table>
            </div>
          </>
        );
    }
  };

  const isCreateDisabled =
    loading ||
    !newEventData.name.trim() ||
    !newEventData.start_datetime ||
    isEndBeforeStart(newEventData.start_datetime, newEventData.end_datetime);

  const isEditDisabled =
    loading ||
    (eventToEdit && !eventToEdit.name.trim()) ||
    (eventToEdit && !eventToEdit.start_datetime) ||
    (eventToEdit &&
      isEndBeforeStart(eventToEdit.start_datetime, eventToEdit.end_datetime));

  const isDeleteDisabled = loading || eventsLoading;

  const globalMessageClassName = `global-message ${apiMessage.type} ${
    isFadingOut ? "fading-out" : ""
  }`;

  const showGameTasksButton = selectedEvent && selectedEvent.game_id;

  return (
    <>
      <div className="clan-events-tab-container">
        <h2>Clan Events Management 🗓️</h2>
        <p className="tab-intro-text">
          <small className="timezone-note">
            &nbsp;(All displayed times are in your local time.)
          </small>
        </p>
        {apiMessage.text &&
          !isCreateModalOpen &&
          !isEditModalOpen &&
          !eventToDelete && (
            <div className={globalMessageClassName}>{apiMessage.text}</div>
          )}

        <div className="event-sub-nav">
          <button
            className={`sub-nav-button ${activeSubView === "list" ? "active" : ""}`}
            onClick={goBackToList}
            disabled={loading}
          >
            <Calendar size={18} />
            <span>Event List</span>
          </button>

          {selectedEvent && activeSubView !== "list" && (
            <>
              <button
                className={`sub-nav-button ${activeSubView === "manage_teams" ? "active" : ""}`}
                onClick={() => switchView("manage_teams", selectedEvent)}
                disabled={loading}
              >
                <Users size={18} />
                <span>Manage Teams</span>
              </button>

              <button
                className={`sub-nav-button ${activeSubView === "manage_game" ? "active" : ""}`}
                onClick={() => switchView("manage_game", selectedEvent)}
                disabled={loading}
              >
                <Gamepad2 size={18} />
                <span>Manage Game</span>
              </button>

              {showGameTasksButton && (
                <button
                  className={`sub-nav-button ${activeSubView === "manage_game_tasks" ? "active" : ""}`}
                  onClick={() => switchView("manage_game_tasks", selectedEvent)}
                  disabled={loading}
                >
                  <ListChecks size={18} />
                  <span>Manage Game Tasks</span>
                </button>
              )}
            </>
          )}

          {/* Only show refresh button when on the list view */}
          {activeSubView === "list" && (
            <button
              className="sub-nav-button refresh-btn"
              onClick={handleManualRefresh}
              disabled={loading || isRefreshing}
              title="Refresh Event Statuses"
            >
              <RefreshCw
                size={18}
                className={isRefreshing ? "spin-icon" : ""}
              />
              <span>Refresh</span>
            </button>
          )}
        </div>

        <div className="event-tab-content-area">{renderActiveSubView()}</div>
      </div>
      <Modal
        isOpen={isCreateModalOpen}
        title={`Schedule a ${DEFAULT_EVENT_TYPE}`}
        message={
          <EventFormContent
            data={newEventData}
            onChange={handleCreateFormChange}
            isEdit={false}
            loading={loading}
            apiMessage={apiMessage}
          />
        }
        onConfirm={executeCreateEvent}
        onCancel={() => {
          closeCreateModal();
          setApiMessage({ type: null, text: null });
        }}
        confirmButtonText={loading ? "Scheduling..." : "Schedule Event"}
        confirmButtonClassName={getConfirmClassName(
          "btn-primary",
          isCreateDisabled,
        )}
      />
      <Modal
        isOpen={isEditModalOpen}
        title="Edit Event Details"
        message={
          eventToEdit && (
            <EventFormContent
              data={eventToEdit}
              onChange={handleEditFormChange}
              isEdit={true}
              loading={loading}
              apiMessage={apiMessage}
            />
          )
        }
        onConfirm={executeEditEvent}
        onCancel={() => {
          closeEditModal();
          setApiMessage({ type: null, text: null });
        }}
        confirmButtonText={loading ? "Updating..." : "Save Changes"}
        confirmButtonClassName={getConfirmClassName(
          "btn-primary",
          isEditDisabled,
        )}
      />
      {eventToDelete && (
        <Modal
          isOpen={!!eventToDelete}
          title="Confirm Deletion"
          message={
            <div className="delete-confirm-message">
              <p>
                Are you sure you want to <strong>permanently delete</strong> the
                event:
              </p>
              <p className="event-name-to-delete">"{eventToDelete.name}"</p>
              <p>This action cannot be undone.</p>
            </div>
          }
          onConfirm={executeDelete}
          onCancel={() => {
            closeDeleteModal();
            setApiMessage({ type: null, text: null });
          }}
          confirmButtonText={
            isDeleteDisabled ? "Processing..." : "Delete Event"
          }
          confirmButtonClassName={getConfirmClassName(
            "btn-danger",
            isDeleteDisabled,
          )}
          cancelButtonText="Cancel"
        />
      )}
    </>
  );
}

export default ClanEventsTab;
